import apiClient from '@/api/apiClient';
import { handleError, getAuthData, prepareModel, validateId } from '../../utils/storeHelpers';

export default {
  namespaced: true,
  state: {
    schema: {},
    catDiagnosticICD10: [],
    catDiagnosticICD11: [],
    dataCrud: [],
    isNewRecord: true,
    status: '',
  },

  getters: {
    schema: (state) => state.schema,
    catDiagnosticICD10: (state) => state.catDiagnosticICD10,
    catDiagnosticICD11: (state) => state.catDiagnosticICD11,
    dataCrud: (state) => state.dataCrud,
    isNewRecord: (state) => state.isNewRecord,
    status: (state) => state.status,
  },

  mutations: {
    SET_SCHEMA(state, payload) {
      state.schema = payload?.schema;
      state.catDiagnosticICD10 = Array.isArray(payload?.catDiagnosticICD10)
        ? payload.catDiagnosticICD10
        : [];
      state.catDiagnosticICD11 = Array.isArray(payload?.catDiagnosticICD11)
        ? payload.catDiagnosticICD11
        : [];
    },
    SET_RECORDS(state, { recordsData }) {
      state.dataCrud = Array.isArray(recordsData?.dataCrud) ? recordsData.dataCrud : [];
      state.isNewRecord = Boolean(recordsData?.isNewRecord);
    },
    SET_STATUS(state, status) {
      state.status = status;
    },
  },

  actions: {
    async fetchSchema({ commit }) {
      try {
        commit('SET_STATUS', 'loading');
        const responses = await Promise.all([
          apiClient.anxAPxMedicalSchema(),
          apiClient.catAgeInterval(),
          apiClient.catDiagnosticManual(),
          apiClient.catDiagnosticICD10(),
          apiClient.catDiagnosticICD11(),
          apiClient.catDuration(),
          apiClient.catProblemTerapyType(),
        ]);
        const [
          schemaRes,
          catAgeInterval,
          catDiagManual,
          catDiagICD10,
          catDiagICD11,
          catDuration,
          catProblemTerapyType,
        ] = responses.map((res) => res?.data ?? {});

        let catDiagnosticManualPre = catDiagManual.data ?? [];
        const catDiagnosticManual = JSON.stringify(catDiagnosticManualPre);

        let catDiagManualDisease = JSON.parse(catDiagnosticManual);
        catDiagManualDisease[0].type = 'disease';
        catDiagManualDisease[1].type = 'disease';
        catDiagManualDisease.splice(2, 1);

        const catDiagnosticICD10 = catDiagICD10.data ?? [];
        const catDiagnosticICD11 = catDiagICD11.data ?? [];

        const catDurationAdjusted1 = (catDuration.data ?? []).slice(77);
        const catDurationAdjusted2 = (catDuration.data ?? []).slice(100);

        if (!schemaRes.formCrud) throw new Error('Estructura de schema inválida.');

        const schema = structuredClone(schemaRes);
        schema.formCrud.back_medical_age.items = catAgeInterval.data ?? [];
        schema.formCrud.back_medical_diagnostic_manual.items = catDiagManualDisease;
        schema.formCrud.back_medical_diagnostic.items = catDiagnosticICD11;
        schema.formCrud.back_medical_duration_id.items = catDurationAdjusted2;
        schema.formCrud.back_medical_hospitaliz_duration_id.items = catDurationAdjusted1;
        schema.formCrud.i1_back_terapy_medical_type.items = catProblemTerapyType.data ?? [];
        schema.formCrud.i1_back_terapy_medical_age_beginning.items = catAgeInterval.data ?? [];
        schema.formCrud.i1_back_terapy_medical_duration_id.items = catDurationAdjusted2;
        schema.formCrud.i2_back_terapy_medical_type.items = catProblemTerapyType.data ?? [];
        schema.formCrud.i2_back_terapy_medical_age_beginning.items = catAgeInterval.data ?? [];
        schema.formCrud.i2_back_terapy_medical_duration_id.items = catDurationAdjusted2;
        schema.formCrud.i3_back_terapy_medical_type.items = catProblemTerapyType.data ?? [];
        schema.formCrud.i3_back_terapy_medical_age_beginning.items = catAgeInterval.data ?? [];
        schema.formCrud.i3_back_terapy_medical_duration_id.items = catDurationAdjusted2;
        schema.formCrud.i4_back_terapy_medical_type.items = catProblemTerapyType.data ?? [];
        schema.formCrud.i4_back_terapy_medical_age_beginning.items = catAgeInterval.data ?? [];
        schema.formCrud.i4_back_terapy_medical_duration_id.items = catDurationAdjusted2;
        schema.formCrud.i5_back_terapy_medical_type.items = catProblemTerapyType.data ?? [];
        schema.formCrud.i5_back_terapy_medical_age_beginning.items = catAgeInterval.data ?? [];
        schema.formCrud.i5_back_terapy_medical_duration_id.items = catDurationAdjusted2;
        schema.formCrud.i6_back_terapy_medical_type.items = catProblemTerapyType.data ?? [];
        schema.formCrud.i6_back_terapy_medical_age_beginning.items = catAgeInterval.data ?? [];
        schema.formCrud.i6_back_terapy_medical_duration_id.items = catDurationAdjusted2;

        commit('SET_SCHEMA', {
          schema,
          catDiagnosticICD10,
          catDiagnosticICD11,
        });
      } catch (error) {
        handleError(error, commit);
      }
    },

    async fetchRecords({ commit, rootState }) {
      try {
        const { clinicalHistories_id, institutions_profes_id } = getAuthData(rootState);
        const res = await apiClient.modelANxAPxMedical(clinicalHistories_id, institutions_profes_id);
        const recordsData = res?.data?.data ?? [];
        commit('SET_RECORDS', { recordsData });
        commit('SET_STATUS', 'loaded');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async createRecord({ commit, rootState }, { model }) {
      try {
        commit('SET_STATUS', 'create');
        const preparedModel = prepareModel(rootState, model);
        await apiClient.createANxAPxMedical(preparedModel);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async updateRecord({ commit, rootState }, { model }) {
      try {
        commit('SET_STATUS', 'update');
        const { clinicalHistories_id } = getAuthData(rootState);
        const preparedModel = prepareModel(rootState, model);
        await apiClient.updateANxAPxMedical(clinicalHistories_id, preparedModel);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async removeRecord({ commit, rootState }, { id }) {
      try {
        commit('SET_STATUS', 'remove');
        const { clinicalHistories_id, institutions_profes_id } = getAuthData(rootState);
        validateId(id);
        await apiClient.deleteANxAPxMedical(id, clinicalHistories_id, institutions_profes_id);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },
  },
};
